import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import * as store from '@attain-apps/fe/web/main/lib/store'

import * as me from '../../../../me'
// NOTE: imported directly rather than through the user-access index, matching
// how the other state that only needs the survey admins reaches them
import * as surveyAdmins from '../../../user-access/survey-admins'
import type { TypeParams } from '../../types'
import { Plugin as AccountDimensionFilterPlugin } from '../account-dimension-filter/plugin'
import { Plugin as ClusterDimensionFilterPlugin } from '../cluster-dimension-filter/plugin'
import { Plugin as EmployeeDimensionFilterPlugin } from '../employee-dimension-filter/plugin'
import { Plugin as OrganizationDimensionFilterPlugin } from '../organization-dimension-filter/plugin'
import { Plugin as RoomTypeDimensionFilterPlugin } from '../room-type-dimension-filter/plugin'
import { Plugin as SpaceDimensionFilterPlugin } from '../space-dimension-filter/plugin'

import { getActionsConfig } from './actions'
import * as effects from './effects'
import * as handlers from './handlers'
import { Selectors } from './selectors'
import { State } from './state'
import type { Edit } from './types'

/**
 * Stores per-report configuration for the Space reports collection, keyed by
 * {@link ents.extra.space.Report.Id}. The dimension filters are not stored
 * here — the dimension-filter plugins own them, and
 * {@link Selectors.getConfiguration} composes in the ones each report declares
 * on read.
 *
 * NOTE: reports don't share a configuration shape, so everything
 * report-specific lives in `handlers.ts` rather than being assumed here
 */
export class Plugin extends ec.plugins.Plugin<
  TypeParams,
  typeof Plugin.id,
  State,
  Selectors,
  ReturnType<typeof getActionsConfig>
> {
  /** @see {@link ec.plugins.Plugin.Id.Custom} */
  public static readonly id = ec.plugins.Plugin.Id.Custom.create(
    'SpaceReportConfiguration',
  )

  /**
   * In-memory settings keyed by report ID. Reading a report that has not been
   * edited yet falls back to its default configuration.
   */
  protected readonly settingsStorage =
    new store.storage.included.InMemoryStorage({
      parent: this.feature,
      properties: {
        lookup: store.storage.lookup<
          ents.extra.space.Report.Id,
          Edit,
          ents.extra.space.Report.Configuration,
          ents.extra.space.Report.Configuration
        >({
          get: (id, stored) =>
            stored ?? ents.extra.space.Report.Configuration.getDefault(id),
          set: (id, edit, current) =>
            handlers.applyEdit(
              id,
              current ?? ents.extra.space.Report.Configuration.getDefault(id),
              edit,
            ),
        }),
      },
      name: 'Settings',
    })

  /**
   * Convenience accessor for the feature's action creators.
   * Allows consumers to call `plugin.actions.xyz` instead of
   * `plugin.feature.actions.xyz`.
   */
  public get actions(): this['feature']['actions'] {
    return this.feature.actions
  }

  /**
   * Convenience accessor for the feature's selectors.
   * Allows consumers to call `plugin.selectors.xyz` instead of
   * `plugin.feature.selectors.xyz`.
   */
  public get selectors(): this['feature']['selectors'] {
    return this.feature.selectors
  }

  constructor({ collection }: Readonly<Plugin.constructor.Params>) {
    const meFeature = ngCore.inject(me.Feature)
    const surveyAdminsEc = ngCore.inject(surveyAdmins.Collection)

    super({
      actions: getActionsConfig(),
      collection,
      id: Plugin.id,
      initialState: State.INITIAL,
      selectors: () =>
        new Selectors(
          this,
          this.settingsStorage,
          {
            account: collection.plugins
              .lookup(AccountDimensionFilterPlugin)
              .get(),
            cluster: collection.plugins
              .lookup(ClusterDimensionFilterPlugin)
              .get(),
            employee: collection.plugins
              .lookup(EmployeeDimensionFilterPlugin)
              .get(),
            organization: collection.plugins
              .lookup(OrganizationDimensionFilterPlugin)
              .get(),
            roomType: collection.plugins
              .lookup(RoomTypeDimensionFilterPlugin)
              .get(),
            space: collection.plugins.lookup(SpaceDimensionFilterPlugin).get(),
          },
          meFeature,
          surveyAdminsEc,
        ),
    })

    this.feature.registerEffects(
      effects.applyEdit(this.settingsStorage),
      effects.resetOnContextChange(this),
      effects.resetSettings(this.settingsStorage),
    )
  }
}

export namespace Plugin {
  export namespace constructor {
    export interface Params {
      readonly collection: ec.Collection<TypeParams>
    }
  }
}
